import { asMoney } from "@bw/shared/types";
import type {
  SoloEntryCreate,
  TournamentRegistrationQuote,
} from "@bw/shared/types/generated";

const STORAGE_PREFIX = "bw:tournament-registration-attempt:";

export type TournamentRegistrationMode = "register" | "solo";
type ConsentAcceptance = NonNullable<SoloEntryCreate["acceptances"]>[number];
type ForeignRepresentation = NonNullable<
  SoloEntryCreate["foreign_representation"]
>;

export interface TournamentRegistrationAttempt {
  eventId: string;
  drawId: string;
  isPaid: boolean;
  paymentMethodId: string | null;
  expectedQuote: TournamentRegistrationQuote;
  partnerId: string | null;
  guardianName: string | null;
  guardianEmail: string | null;
  acceptLegalTerms: boolean;
  acceptances: ConsentAcceptance[];
  foreignRepresentation: ForeignRepresentation | null;
}

function storageKey(
  facilityId: string,
  userId: string,
  eventId: string,
  mode: TournamentRegistrationMode,
): string {
  return [STORAGE_PREFIX, facilityId, userId, eventId, mode]
    .map(encodeURIComponent)
    .join(":");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function parseAttempt(value: unknown): TournamentRegistrationAttempt | null {
  if (
    !isRecord(value) ||
    typeof value.eventId !== "string" ||
    typeof value.drawId !== "string" ||
    value.isPaid !== true ||
    typeof value.paymentMethodId !== "string" ||
    !isRecord(value.expectedQuote) ||
    typeof value.expectedQuote.amount !== "string" ||
    typeof value.expectedQuote.currency !== "string" ||
    value.expectedQuote.requires_payment !== true ||
    !isNullableString(value.partnerId) ||
    !isNullableString(value.guardianName) ||
    !isNullableString(value.guardianEmail) ||
    typeof value.acceptLegalTerms !== "boolean" ||
    !Array.isArray(value.acceptances)
  ) {
    return null;
  }

  let expectedAmount: ReturnType<typeof asMoney>;
  try {
    expectedAmount = asMoney(value.expectedQuote.amount);
  } catch {
    return null;
  }
  if (expectedAmount === null) return null;

  const acceptances: ConsentAcceptance[] = [];
  for (const acceptance of value.acceptances) {
    if (
      !isRecord(acceptance) ||
      typeof acceptance.document_id !== "string" ||
      !Number.isInteger(acceptance.version) ||
      typeof acceptance.guardian_signature !== "boolean"
    ) {
      return null;
    }
    acceptances.push({
      document_id: acceptance.document_id,
      version: acceptance.version as number,
      guardian_signature: acceptance.guardian_signature,
      ...(typeof acceptance.ip_address === "string"
        ? { ip_address: acceptance.ip_address }
        : {}),
    });
  }

  const rawForeignRepresentation = value.foreignRepresentation;
  let foreignRepresentation: ForeignRepresentation | null = null;
  if (rawForeignRepresentation !== null) {
    if (
      !isRecord(rawForeignRepresentation) ||
      typeof rawForeignRepresentation.declared !== "boolean" ||
      (rawForeignRepresentation.countries !== undefined &&
        !Array.isArray(rawForeignRepresentation.countries))
    ) {
      return null;
    }

    const countries: ForeignRepresentation["countries"] = [];
    for (const country of rawForeignRepresentation.countries ?? []) {
      if (
        !isRecord(country) ||
        typeof country.country !== "string" ||
        (country.last_event !== undefined &&
          !isNullableString(country.last_event)) ||
        (country.period !== undefined && !isNullableString(country.period))
      ) {
        return null;
      }
      countries.push({
        country: country.country,
        ...(country.last_event !== undefined
          ? { last_event: country.last_event }
          : {}),
        ...(country.period !== undefined ? { period: country.period } : {}),
      });
    }
    foreignRepresentation = {
      declared: rawForeignRepresentation.declared,
      ...(rawForeignRepresentation.countries !== undefined
        ? { countries }
        : {}),
    };
  }

  return {
    eventId: value.eventId,
    drawId: value.drawId,
    isPaid: true,
    paymentMethodId: value.paymentMethodId,
    expectedQuote: {
      amount: expectedAmount,
      currency: value.expectedQuote.currency,
      requires_payment: true,
    },
    partnerId: value.partnerId,
    guardianName: value.guardianName,
    guardianEmail: value.guardianEmail,
    acceptLegalTerms: value.acceptLegalTerms,
    acceptances,
    foreignRepresentation,
  };
}

function sameValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((value, index) => sameValue(value, right[index]))
    );
  }
  if (isRecord(left) && isRecord(right)) {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every(
        (key, index) =>
          key === rightKeys[index] && sameValue(left[key], right[key]),
      )
    );
  }
  return false;
}

export function loadTournamentRegistrationAttempt(
  facilityId: string,
  userId: string,
  eventId: string,
  mode: TournamentRegistrationMode,
): TournamentRegistrationAttempt | null {
  const key = storageKey(facilityId, userId, eventId, mode);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const envelope: unknown = JSON.parse(raw);
    if (
      !isRecord(envelope) ||
      envelope.facilityId !== facilityId ||
      envelope.userId !== userId ||
      envelope.eventId !== eventId ||
      envelope.mode !== mode
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    const attempt = parseAttempt(envelope.attempt);
    if (attempt === null || attempt.eventId !== eventId) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return attempt;
  } catch {
    return null;
  }
}

export function storeTournamentRegistrationAttempt(
  facilityId: string,
  userId: string,
  eventId: string,
  mode: TournamentRegistrationMode,
  attempt: TournamentRegistrationAttempt,
): void {
  try {
    window.sessionStorage.setItem(
      storageKey(facilityId, userId, eventId, mode),
      JSON.stringify({ facilityId, userId, eventId, mode, attempt }),
    );
  } catch {
    // Mounted state still owns the exact request if storage is unavailable.
  }
}

export function clearTournamentRegistrationAttempt(
  facilityId: string,
  userId: string,
  eventId: string,
  mode: TournamentRegistrationMode,
  expected: TournamentRegistrationAttempt,
): void {
  const stored = loadTournamentRegistrationAttempt(
    facilityId,
    userId,
    eventId,
    mode,
  );
  if (stored === null || !sameValue(stored, expected)) return;
  try {
    window.sessionStorage.removeItem(
      storageKey(facilityId, userId, eventId, mode),
    );
  } catch {
    // Storage may become unavailable after the attempt was written.
  }
}
